import { APP_BUSINESS_DICTIONARY_CODES } from '@/constant/dynamic-form-constants';

/**
 * Response thật của executor `HRM_GetThongTinGiangVien` (mảng, luôn 1 phần tử
 * ứng với `MaNhanSu` đã gửi).
 */
export interface IHrmThongTinGiangVienResponse {
    HoTen: string;
    GioiTinh: string;
    DiaChiThuongTru: string;
    DienThoaiDiDong: string;
    CCCD: string;
    BoPhanCongTac: string;
    ChucVu: string;
    /** Tên trưởng đơn vị cấp 2 */
    TruongDonViCap2: string;
    /** Tên trưởng đơn vị cấp 3 */
    TruongDonViCap3: string;
    /**
     * Mã phân loại cán bộ (ổn định) — tra `mapMaPhanLoaiToStaffCategory` để ra
     * nhóm chuẩn hoá. DÙNG MÃ NÀY để so khớp, KHÔNG dùng tên `PhanLoai`.
     */
    MaPhanLoai: string;
    /** Tên phân loại cán bộ (chỉ để hiển thị — không dùng để so khớp) */
    PhanLoai: string;
}

/**
 * Nhóm đối tượng cán bộ, suy ra từ `MaPhanLoai` trả về bởi HRM.
 *
 * Giá trị enum = ĐÚNG mã `ProcedureType.ValueStr` (và token trong
 * `procedure.Types_Token`) do admin khai ở màn hình "Cấu hình loại thủ tục":
 *  - `employee`                  → Cán bộ / viên chức không giữ chức vụ
 *  - `employee_manager`          → Cán bộ lãnh đạo, quản lý
 *  - `employee_management_board` → Ban Giám hiệu
 *
 * Cả 3 đều bắt đầu bằng prefix `ProcedureTypeStartingCodes.User` = `employee`
 * nên vẫn qua `startsWithCode` cho role Staff; phần đuôi mới là cái phân biệt
 * nhóm (khớp với `staffCategory`, xem `isProcedureTypeEligibleForUser`).
 */
export enum HrmStaffCategory {
    /** Viên chức / người lao động không giữ chức vụ */
    Employee = 'employee',
    /** Viên chức lãnh đạo, quản lý */
    Manager = 'employee_manager',
    /** Ban Giám hiệu */
    ManagementBoard = 'employee_management_board',
}

/**
 * Map `MaPhanLoai` (mã ổn định từ HRM) → `HrmStaffCategory` chuẩn hoá.
 * Khớp CHÍNH XÁC theo mã — 3 giá trị đã xác nhận từ danh mục HRM:
 * - "VC_NLD"             → Employee
 * - "VCNLD_ChucVuQuanLy" → Manager
 * - "BanGiamHieu"        → ManagementBoard
 *
 * `MaPhanLoai` lạ/chưa khai báo → `undefined` (caller coi như KHÔNG thuộc nhóm
 * nào đã biết — an toàn hơn là đoán bừa 1 nhóm).
 */
export function mapMaPhanLoaiToStaffCategory(
    maPhanLoai?: string
): HrmStaffCategory | undefined {
    switch (maPhanLoai) {
        case 'VC_NLD':
            return HrmStaffCategory.Employee;
        case 'VCNLD_ChucVuQuanLy':
            return HrmStaffCategory.Manager;
        case 'BanGiamHieu':
            return HrmStaffCategory.ManagementBoard;
        default:
            return undefined;
    }
}

/** Chữ ký gọi executor backend qua tRPC — khớp `ExecuteApiFn` (business-rules) và `executeApi` (modal-print-decision). */
export type ExecuteHrmApiFn = (
    value: string,
    params?: Record<string, any>
) => Promise<{
    isSuccessStatus?: boolean;
    data?: any;
    errors?: string[];
}>;

/**
 * Gọi `HRM_GetThongTinGiangVien` theo mã nhân sự (`CivilServantCode` trong
 * thông tin tài khoản cán bộ) → trả về phần tử đầu tiên của mảng response
 * (luôn đúng 1 cán bộ ứng với 1 mã). `undefined` nếu thiếu mã hoặc API lỗi.
 */
export async function fetchThongTinGiangVien(
    executeApi: ExecuteHrmApiFn,
    civilServantCode?: string
): Promise<IHrmThongTinGiangVienResponse | undefined> {
    if (!civilServantCode) return undefined;

    const res = await executeApi(
        APP_BUSINESS_DICTIONARY_CODES.hrm_get_thong_tin_giang_vien,
        { MaNhanSu: civilServantCode }
    );

    if (!res?.isSuccessStatus || !Array.isArray(res.data)) return undefined;
    return res.data[0] as IHrmThongTinGiangVienResponse | undefined;
}
